import { SemanticCOLORS, Loader, Icon as SemanticIcon } from 'semantic-ui-react';
import { FormValidators } from '@servicetitan/form';
import * as Styles from './password-requirements.module.less';
import { CompromisedPasswordValidator } from './compromised-password-validator';
import { Tooltip, Icon, Stack } from '@servicetitan/design-system';
import { tokens } from '@servicetitan/tokens/core';
import classNames from 'classnames';

interface PasswordValidationChecklistElementProps {
    isValid: boolean;
    isActive: boolean;
    requirementText: string;
    isLoading?: boolean;
    tooltip?: string;
}

export const PasswordValidationChecklistElement = (
    props: PasswordValidationChecklistElementProps
) => {
    const { isValid, isActive, isLoading } = props;

    let colorState: SemanticCOLORS;

    if (!isActive) {
        colorState = '#909090' as SemanticCOLORS;
    } else if (isValid) {
        colorState = 'green';
    } else {
        colorState = 'red';
    }

    const checklistElement = (
        <div style={{ marginBottom: '3px' }}>
            {isActive && !isLoading && (
                <SemanticIcon name={isValid ? 'checkmark' : 'remove'} color={colorState} />
            )}
            {isLoading && <Loader className="m-r-half-i" size="tiny" inline active />}
            <span style={{ color: colorState }}>{props.requirementText}</span>
        </div>
    );

    return !props.tooltip ? (
        checklistElement
    ) : (
        <Stack>
            {checklistElement}
            <Tooltip direction="right" text={props.tooltip} el="span" className="d-f-i">
                <Icon
                    name="info"
                    size={20}
                    className="m-l-1-i align-self-start-i"
                    color={tokens.colorBlue300}
                />
            </Tooltip>
        </Stack>
    );
};

export interface PasswordRequirementsProps {
    password: string;
    confirmedPassword: string;
    isPasswordVisible: boolean;
    disablePasswordConfirm?: boolean;
    className?: string;
}

export const PasswordRequirements = (props: PasswordRequirementsProps) => {
    const inputFieldsAreEmpty = props.password.length === 0 && props.confirmedPassword.length === 0;
    return (
        <div className={classNames(Styles.passwordRequirementContainer, props.className)}>
            <PasswordValidationChecklistElement
                requirementText="Use 10 or more characters"
                isValid={props.password.length > 9}
                isActive={!inputFieldsAreEmpty}
            />
            <PasswordValidationChecklistElement
                requirementText="Use at least one uppercase and one lowercase letter"
                isValid={
                    FormValidators.hasLowerCase(props.password) &&
                    FormValidators.hasUpperCase(props.password)
                }
                isActive={!inputFieldsAreEmpty}
            />
            <PasswordValidationChecklistElement
                requirementText="Use at least one digit (0-9)"
                isValid={FormValidators.hasNumber(props.password)}
                isActive={!inputFieldsAreEmpty}
            />
            {!props.disablePasswordConfirm && (
                <PasswordValidationChecklistElement
                    requirementText="Passwords must match"
                    isValid={
                        props.password.length !== 0 &&
                        props.confirmedPassword.length !== 0 &&
                        props.password === props.confirmedPassword
                    }
                    isActive={!inputFieldsAreEmpty}
                />
            )}
            <CompromisedPasswordValidator password={props.password} />
        </div>
    );
};
